import React, { useEffect } from 'react';

const IteratorPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Iterator Pattern - Behavioral Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Iterator Pattern - provide a way to access elements of a collection sequentially without exposing its internal structure. Learn with playlist and tree examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Iterator Pattern - provide a way to access elements of a collection sequentially without exposing its internal structure. Learn with playlist and tree examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'iterator pattern, design patterns, behavioral patterns, collection traversal, sequential access, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'iterator pattern, design patterns, behavioral patterns, collection traversal, sequential access, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Iterator Pattern</h1>
        <p style={styles.subtitle}>
          Behavioral Design Pattern - Access collection elements sequentially
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Iterator pattern provides a way to access the elements of a collection sequentially 
            without exposing its underlying representation. It separates the traversal logic from 
            the collection, allowing multiple traversals to occur simultaneously.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You need to traverse a collection without exposing its internal structure. Different 
            collections might require different traversal algorithms, and you want to provide 
            a uniform interface for accessing elements regardless of the collection type.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Music Playlist</h2>
          <div style={styles.codeBlock}>
{`// Iterator interface
interface Iterator<T> {
    boolean hasNext();
    T next();
    void remove();
}

// Iterable interface
interface IterableCollection<T> {
    Iterator<T> createIterator();
}

// Song class
class Song {
    private String title;
    private String artist;
    private int duration; // in seconds
    
    public Song(String title, String artist, int duration) {
        this.title = title;
        this.artist = artist;
        this.duration = duration;
    }
    
    public String getTitle() { return title; }
    public String getArtist() { return artist; }
    public int getDuration() { return duration; }
    
    @Override
    public String toString() {
        return title + " by " + artist + " (" + duration/60 + ":" + 
               String.format("%02d", duration%60) + ")";
    }
}

// Concrete collection
class Playlist implements IterableCollection<Song> {
    private List<Song> songs;
    private String name;
    
    public Playlist(String name) {
        this.name = name;
        this.songs = new ArrayList<>();
    }
    
    public void addSong(Song song) {
        songs.add(song);
    }
    
    public void removeSong(Song song) {
        songs.remove(song);
    }
    
    public String getName() { return name; }
    public int size() { return songs.size(); }
    
    @Override
    public Iterator<Song> createIterator() {
        return new PlaylistIterator();
    }
    
    // Concrete iterator as inner class
    private class PlaylistIterator implements Iterator<Song> {
        private int currentPosition = 0;
        
        @Override
        public boolean hasNext() {
            return currentPosition < songs.size();
        }
        
        @Override
        public Song next() {
            if (!hasNext()) {
                throw new NoSuchElementException("No more songs in playlist");
            }
            return songs.get(currentPosition++);
        }
        
        @Override
        public void remove() {
            if (currentPosition <= 0) {
                throw new IllegalStateException("Cannot remove before calling next()");
            }
            songs.remove(--currentPosition);
        }
    }
    
    // Additional iterator for random access
    public Iterator<Song> createShuffleIterator() {
        return new ShuffleIterator();
    }
    
    private class ShuffleIterator implements Iterator<Song> {
        private List<Integer> shuffledIndices;
        private int currentPosition = 0;
        
        public ShuffleIterator() {
            shuffledIndices = new ArrayList<>();
            for (int i = 0; i < songs.size(); i++) {
                shuffledIndices.add(i);
            }
            Collections.shuffle(shuffledIndices);
        }
        
        @Override
        public boolean hasNext() {
            return currentPosition < shuffledIndices.size();
        }
        
        @Override
        public Song next() {
            if (!hasNext()) {
                throw new NoSuchElementException("No more songs in shuffle");
            }
            int index = shuffledIndices.get(currentPosition++);
            return songs.get(index);
        }
        
        @Override
        public void remove() {
            throw new UnsupportedOperationException("Remove not supported in shuffle mode");
        }
    }
}

// Music player using iterator
class MusicPlayer {
    public void playPlaylist(Playlist playlist, boolean shuffle) {
        System.out.println("Playing playlist: " + playlist.getName());
        System.out.println("Songs: " + playlist.size());
        System.out.println("Mode: " + (shuffle ? "Shuffle" : "Sequential"));
        System.out.println();
        
        Iterator<Song> iterator = shuffle ? 
            playlist.createShuffleIterator() : 
            playlist.createIterator();
        
        int trackNumber = 1;
        while (iterator.hasNext()) {
            Song song = iterator.next();
            System.out.println(trackNumber + ". Now playing: " + song);
            trackNumber++;
            
            // Simulate playing (just for demo)
            if (trackNumber > 3) { // Stop after 3 songs for demo
                System.out.println("... (stopping demo after 3 songs)");
                break;
            }
        }
    }
    
    public void searchAndPlay(Playlist playlist, String searchTerm) {
        System.out.println("\\nSearching for: " + searchTerm);
        Iterator<Song> iterator = playlist.createIterator();
        boolean found = false;
        
        while (iterator.hasNext()) {
            Song song = iterator.next();
            if (song.getTitle().toLowerCase().contains(searchTerm.toLowerCase()) ||
                song.getArtist().toLowerCase().contains(searchTerm.toLowerCase())) {
                System.out.println("Found and playing: " + song);
                found = true;
                break;
            }
        }
        
        if (!found) {
            System.out.println("No songs found matching: " + searchTerm);
        }
    }
}

// Usage
public class IteratorDemo {
    public static void main(String[] args) {
        // Create playlist
        Playlist rockPlaylist = new Playlist("Classic Rock Hits");
        rockPlaylist.addSong(new Song("Bohemian Rhapsody", "Queen", 355));
        rockPlaylist.addSong(new Song("Stairway to Heaven", "Led Zeppelin", 482));
        rockPlaylist.addSong(new Song("Hotel California", "Eagles", 391));
        rockPlaylist.addSong(new Song("Sweet Child O' Mine", "Guns N' Roses", 356));
        rockPlaylist.addSong(new Song("Smoke on the Water", "Deep Purple", 338));
        
        MusicPlayer player = new MusicPlayer();
        
        // Play in sequential order
        player.playPlaylist(rockPlaylist, false);
        
        System.out.println("\\n" + "=".repeat(50) + "\\n");
        
        // Play in shuffle mode
        player.playPlaylist(rockPlaylist, true);
        
        // Search functionality
        player.searchAndPlay(rockPlaylist, "Queen");
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Uniform Interface:</strong> Same interface for different collection types</li>
            <li style={styles.listItem}><strong>Encapsulation:</strong> Hides internal collection structure</li>
            <li style={styles.listItem}><strong>Multiple Traversals:</strong> Can have multiple iterators on same collection</li>
            <li style={styles.listItem}><strong>Single Responsibility:</strong> Separates traversal logic from collection</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you need to traverse a collection without exposing its structure</li>
            <li style={styles.listItem}>When you want to provide multiple ways to traverse the same collection</li>
            <li style={styles.listItem}>When you need to support multiple simultaneous traversals</li>
            <li style={styles.listItem}>When you want to provide a uniform interface for different collection types</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Iterator pattern is fundamental to many programming languages and frameworks. 
            It provides a clean, consistent way to traverse collections while maintaining 
            encapsulation and supporting multiple traversal strategies.
          </p>
        </section>
      </main>
    </article>
  );
};

export default IteratorPattern;
